import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { Text, Image, Card, CardColors } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

const steps = [
  {
    title: 'Prepare for catch-up bookkeeping',
    description: [
      'Add any other accounts used for your business',
      'Let us know if you have personal transactions in your account(s)',
      'Upload bank statements to set up automatic reconciliation',
    ],
  },
  {
    title: 'Catch-up bookkeeping',
    description:
      "We'll handle the rest! Your books will be professionally categorized and reconciled by our team.",
  },
]

export const H1GetStartedUpdatesObScreen = () => {
  const currentUser = useReselector(getCurrentUser)

  return (
    <div className="max-w-6xl mx-auto flex flex-col gap-12">
      <Card
        color={CardColors.SUCCESS}
        className="flex justify-center rounded-2xl p-6 my-0"
      >
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/book-celebration.svg"
          alt=""
          className="w-[180px] h-auto"
        />
      </Card>

      <div className="text-center flex flex-col items-center gap-4">
        <Text
          variant={TypographyStyles.DISPLAY_LG}
          color={TextColors.HEADER}
          className="font-extralight text-center"
        >
          Welcome to Heard
          {currentUser?.firstName ? `, ${currentUser.firstName}` : ''}!
        </Text>
        <div className="max-w-2xl mx-auto">
          <Text variant={TypographyStyles.BODY_SM}>
            We&apos;re so excited you&apos;re here. At Heard, we&apos;re on a
            mission to lighten the mental load of running your practice, so you
            can focus on what you do best.
          </Text>
        </div>
      </div>

      <div className="flex flex-col">
        <Text variant={TypographyStyles.H1} className="text-neutral-900">
          Up next
        </Text>

        <Card className="bg-gradient-blue">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="flex flex-col items-center lg:items-start gap-6">
              <div className="mb-6">
                <Image
                  src="https://heard-images.s3.amazonaws.com/assets/books_lightShadow.png"
                  alt=""
                  className="w-[200px] h-auto"
                />
              </div>
              <div className="text-center lg:text-left">
                <Text
                  variant={TypographyStyles.DISPLAY_SM}
                  color={TextColors.HEADER}
                >
                  Catch-up bookkeeping
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  A complete review of your books from January-April 2025.
                </Text>
              </div>
              <div className="flex flex-col gap-2">
                <Text variant={TypographyStyles.BODY_SM}>
                  Catch-up bookkeeping is a <strong>one-time</strong> process in
                  which we set up your account and ensure your books are
                  audit-proof.
                </Text>
              </div>
            </div>

            <div className="flex flex-col">
              <Text
                variant={TypographyStyles.EYEBROW}
                className="text-gray-500 uppercase tracking-wide text-neutral-900"
              >
                HOW IT WORKS
              </Text>

              <div>
                {steps.map((step, index) => (
                  <Card key={step.title} className="p-6 shadow-sm">
                    <div>
                      <div className="flex flex-col gap-3">
                        <div className="flex gap-2 items-start">
                          <div
                            className={`w-6 h-6 flex items-center justify-center ${
                              index === 0 ? 'bg-orange-100' : 'bg-neutral-100'
                            }  rounded-full font-bold text-sm flex-shrink-0`}
                          >
                            <Text
                              variant={TypographyStyles.BODY_XS}
                              color={
                                index === 0
                                  ? TextColors.WHITE
                                  : TextColors.EMPHASIS
                              }
                            >
                              {index + 1}
                            </Text>
                          </div>
                          <Text
                            variant={TypographyStyles.H3}
                            className="text-neutral-900"
                          >
                            {step.title}
                          </Text>
                        </div>
                        {Array.isArray(step.description) ? (
                          <ul className="space-y-1">
                            {step.description.map((item) => (
                              <li key={item} className="flex items-start gap-2">
                                <Text
                                  variant={TypographyStyles.BODY_SM}
                                  color={TextColors.MUTED}
                                >
                                  •
                                </Text>
                                <Text
                                  variant={TypographyStyles.BODY_SM}
                                  color={TextColors.MUTED}
                                >
                                  {item}
                                </Text>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            color={TextColors.MUTED}
                          >
                            {step.description}
                          </Text>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
